import { yupResolver } from '@hookform/resolvers/yup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, Grid } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import './styles.scss';
import { useSnackbar } from '../../../components/Snackbar/hooks';
import { AuthHeader } from '../components/AuthHeader';
import { WithForgotPasswordModal } from '../components/ForgotPasswordModal';
import {
  ForgotPasswordModalButtonProps,
  ForgotPasswordModalProps,
} from '../components/ForgotPasswordModal/interface';
import { TermsOfServiceLabel } from '../components/TermsOfServiceLabel';
import { WelcomeContainer } from '../components/WelcomeContainer';

import { ForgotPasswordFormKeys } from './interfaces';
import { ForgotPasswordValidationSchema } from './validations';

import { Button } from '@components/ButtonV2';
import InputComponent from '@components/InputComponent';
import { useAppSelector } from '@hooks/state';
import { forgotPasswordRequest } from '@state/auth/actions';
import { getRequestError, isRequestRunning } from '@state/requests/selectors';
import { FormTextInputLengths } from '@utils/FormInputLengths';

export const ForgotPasswordPage = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  const { loading, error } = useAppSelector((state) => ({
    loading: isRequestRunning(state, String(forgotPasswordRequest)),
    error: getRequestError(state, String(forgotPasswordRequest)),
  }));

  const initialValues: ForgotPasswordFormKeys = {
    email: '',
  };

  const { control, handleSubmit, setError, reset, formState } =
    useForm<ForgotPasswordFormKeys>({
      resolver: yupResolver(ForgotPasswordValidationSchema),
      defaultValues: initialValues,
    });

  const submitButtonProps: ForgotPasswordModalButtonProps = {
    loading,
    disabled: loading,
    'data-testid': 'sendPasswordResetButton',
    variant: 'contained',
    color: 'primary',
    label: 'Send Password Reset',
  };

  const handleGoBackClick = () => navigate('/login');

  const handleSubmitForm = (data: ForgotPasswordFormKeys) => {
    dispatch(
      forgotPasswordRequest({
        ...data,
        callback: () => {
          showMessage(
            'Done! If you have entered a registered email you will receive a link to Create a New Password.',
            { severity: 'info' },
          );
          reset();
        },
      }),
    );
  };

  const WithForgotPasswordModalProps: ForgotPasswordModalProps = {
    handleSubmit,
    handleSubmitForm,
  };

  useEffect(() => {
    if (error.email)
      setError('email', {
        type: 'Invalid',
        message: error.email[0],
      });
  }, [error]);

  return (
    <WelcomeContainer>
      <Box className='bl-forgot-password-container'>
        <AuthHeader
          title='Forgot Password?'
          subTitle='Fill your email address and you will receive a link to reset your
              password.'
        />
        <form onSubmit={handleSubmit(handleSubmitForm)} className='form'>
          <Grid container>
            <Grid item xs={12}>
              <Controller
                name='email'
                control={control}
                render={({ field, fieldState }) => (
                  <InputComponent
                    variant='outlined'
                    id='email'
                    label='Email address'
                    data-testid='email'
                    name={field.name}
                    onBlur={field.onBlur}
                    placeholder='Enter email address'
                    onChange={field.onChange}
                    value={field.value}
                    error={!!fieldState.error}
                    helperText={formState.errors.email?.message}
                    inputProps={{
                      maxLength: FormTextInputLengths.profile.email,
                    }}
                    keepHelperTextSpace
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} className='footer-buttons'>
              {WithForgotPasswordModal(submitButtonProps)(Button)(
                WithForgotPasswordModalProps,
              )}
              <Button
                variant='text'
                color='primary'
                data-testid='goBackButton'
                startIcon={<ChevronLeftIcon />}
                onClick={handleGoBackClick}
              >
                Go back to log in
              </Button>
            </Grid>
          </Grid>
        </form>
        <TermsOfServiceLabel />
      </Box>
    </WelcomeContainer>
  );
};
